import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { AdTopBanner, AdInContent, AdBottomBanner } from '../components/AdSense';

const PaceCalculatorPage = () => {
  const [calcMode, setCalcMode] = useState('pace'); // pace, time, distance
  const [distance, setDistance] = useState('5');
  const [distanceUnit, setDistanceUnit] = useState('km');
  const [hours, setHours] = useState('0');
  const [minutes, setMinutes] = useState('25');
  const [seconds, setSeconds] = useState('0');
  const [paceMin, setPaceMin] = useState('5');
  const [paceSec, setPaceSec] = useState('0');
  const [paceUnit, setPaceUnit] = useState('km');
  const [result, setResult] = useState(null);

  const calculate = () => {
    const dist = parseFloat(distance) || 0;
    const totalSeconds = (parseInt(hours) || 0) * 3600 + (parseInt(minutes) || 0) * 60 + (parseInt(seconds) || 0);
    const paceSeconds = (parseInt(paceMin) || 0) * 60 + (parseInt(paceSec) || 0);

    if (calcMode === 'pace') {
      if (dist > 0 && totalSeconds > 0) {
        const pace = totalSeconds / dist;
        const paceMinutes = Math.floor(pace / 60);
        const paceSecVal = Math.round(pace % 60);
        const speedKmh = (dist / totalSeconds) * 3600 * (distanceUnit === 'mi' ? 1.60934 : 1);
        const speedMph = speedKmh / 1.60934;
        
        setResult({
          type: 'pace',
          paceMin: paceMinutes,
          paceSec: paceSecVal,
          unit: distanceUnit,
          speedKmh: speedKmh.toFixed(2),
          speedMph: speedMph.toFixed(2)
        });
      }
    } else if (calcMode === 'time') {
      if (dist > 0 && paceSeconds > 0) {
        const totalTime = dist * paceSeconds;
        const h = Math.floor(totalTime / 3600);
        const m = Math.floor((totalTime % 3600) / 60);
        const s = Math.round(totalTime % 60);
        
        setResult({
          type: 'time',
          hours: h,
          minutes: m,
          seconds: s
        });
      }
    } else if (calcMode === 'distance') {
      if (totalSeconds > 0 && paceSeconds > 0) {
        const calculatedDist = totalSeconds / paceSeconds;
        setResult({
          type: 'distance',
          distance: calculatedDist.toFixed(2),
          unit: paceUnit
        });
      }
    }
  };

  const commonDistances = [
    { name: '5K', km: 5, mi: 3.1 },
    { name: '10K', km: 10, mi: 6.2 },
    { name: 'Half Marathon', km: 21.0975, mi: 13.1 },
    { name: 'Marathon', km: 42.195, mi: 26.2 }
  ];

  return (
    <div className="min-h-screen bg-[#0d0d0d] flex flex-col">
      <Header />
      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <AdTopBanner />
          
          <nav className="text-sm text-gray-500 mb-4">
            <Link to="/home" className="text-cyan-400 hover:text-cyan-300">Home</Link>
            <span className="mx-2">›</span>
            <span className="text-gray-400">Pace Calculator</span>
          </nav>

          <h1 className="text-3xl font-bold text-white mb-4">Pace Calculator</h1>
          <p className="text-gray-400 mb-6">Calculate your running or walking pace, finish time, or distance.</p>

          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
            <div className="flex border-b border-[#2a2a2a] mb-6 overflow-x-auto">
              {[{ id: 'pace', label: 'Calculate Pace' }, { id: 'time', label: 'Calculate Time' }, { id: 'distance', label: 'Calculate Distance' }].map(mode => (
                <button key={mode.id} onClick={() => setCalcMode(mode.id)}
                  className={`px-4 py-2 font-medium text-sm border-b-2 whitespace-nowrap ${calcMode === mode.id ? 'border-cyan-500 text-cyan-400' : 'border-transparent text-gray-400'}`}>
                  {mode.label}
                </button>
              ))}
            </div>

            <div className="space-y-4">
              {(calcMode === 'pace' || calcMode === 'time') && (
                <div className="flex flex-wrap items-center gap-4">
                  <label className="text-gray-300 w-20">Distance</label>
                  <Input type="number" step="0.01" value={distance} onChange={(e) => setDistance(e.target.value)}
                    className="w-24 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                  <select value={distanceUnit} onChange={(e) => setDistanceUnit(e.target.value)}
                    className="px-3 py-2 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-white">
                    <option value="km">km</option>
                    <option value="mi">mi</option>
                  </select>
                  <div className="flex gap-2">
                    {commonDistances.map(d => (
                      <button key={d.name} onClick={() => {
                        setDistance(distanceUnit === 'km' ? d.km.toString() : d.mi.toString());
                      }} className="px-2 py-1 text-xs bg-[#2a2a2a] text-gray-300 rounded hover:bg-[#3a3a3a]">
                        {d.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {(calcMode === 'pace' || calcMode === 'distance') && (
                <div className="flex flex-wrap items-center gap-4">
                  <label className="text-gray-300 w-20">Time</label>
                  <Input type="number" value={hours} onChange={(e) => setHours(e.target.value)}
                    className="w-16 bg-[#0d0d0d] border-[#2a2a2a] text-white text-center" />
                  <span className="text-gray-400">h</span>
                  <Input type="number" value={minutes} onChange={(e) => setMinutes(e.target.value)}
                    className="w-16 bg-[#0d0d0d] border-[#2a2a2a] text-white text-center" />
                  <span className="text-gray-400">m</span>
                  <Input type="number" value={seconds} onChange={(e) => setSeconds(e.target.value)}
                    className="w-16 bg-[#0d0d0d] border-[#2a2a2a] text-white text-center" />
                  <span className="text-gray-400">s</span>
                </div>
              )}

              {(calcMode === 'time' || calcMode === 'distance') && (
                <div className="flex flex-wrap items-center gap-4">
                  <label className="text-gray-300 w-20">Pace</label>
                  <Input type="number" value={paceMin} onChange={(e) => setPaceMin(e.target.value)}
                    className="w-16 bg-[#0d0d0d] border-[#2a2a2a] text-white text-center" />
                  <span className="text-gray-400">:</span>
                  <Input type="number" value={paceSec} onChange={(e) => setPaceSec(e.target.value)}
                    className="w-16 bg-[#0d0d0d] border-[#2a2a2a] text-white text-center" />
                  <span className="text-gray-400">per</span>
                  <select value={paceUnit} onChange={(e) => setPaceUnit(e.target.value)}
                    className="px-3 py-2 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-white">
                    <option value="km">km</option>
                    <option value="mi">mi</option>
                  </select>
                </div>
              )}
            </div>

            <Button onClick={calculate} className="mt-6 bg-cyan-600 hover:bg-cyan-500 text-white px-8">Calculate</Button>

            {result && (
              <div className="mt-6 p-5 bg-[#0d0d0d] border border-[#2a2a2a] rounded-xl">
                <div className="text-center">
                  {result.type === 'pace' && (
                    <>
                      <p className="text-gray-400 mb-2">Your Pace</p>
                      <p className="text-4xl font-bold text-green-400">
                        {result.paceMin}:{result.paceSec.toString().padStart(2, '0')} <span className="text-xl">per {result.unit}</span>
                      </p>
                      <div className="mt-4 flex justify-center gap-4">
                        <div className="p-3 bg-[#1a1a1a] rounded-lg">
                          <p className="text-gray-400 text-sm">Speed</p>
                          <p className="text-white font-bold">{result.speedKmh} km/h</p>
                        </div>
                        <div className="p-3 bg-[#1a1a1a] rounded-lg">
                          <p className="text-gray-400 text-sm">Speed</p>
                          <p className="text-white font-bold">{result.speedMph} mph</p>
                        </div>
                      </div>
                    </>
                  )}
                  {result.type === 'time' && (
                    <>
                      <p className="text-gray-400 mb-2">Finish Time</p>
                      <p className="text-4xl font-bold text-green-400">
                        {result.hours > 0 ? `${result.hours}:` : ''}{result.minutes.toString().padStart(2, '0')}:{result.seconds.toString().padStart(2, '0')}
                      </p>
                    </>
                  )}
                  {result.type === 'distance' && (
                    <>
                      <p className="text-gray-400 mb-2">Distance</p>
                      <p className="text-4xl font-bold text-green-400">{result.distance} {result.unit}</p>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          <AdInContent />

          <div className="mt-10 p-6 bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl">
            <h3 className="text-lg font-semibold text-white mb-4">Fitness & Health Calculators</h3>
            <div className="flex flex-wrap gap-2">
              {[{ name: 'BMI', path: '/bmi-calculator' }, { name: 'Calorie', path: '/calorie-calculator' }, { name: 'BMR', path: '/bmr-calculator' }].map((calc) => (
                <Link key={calc.path} to={calc.path} className="px-3 py-1.5 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-cyan-400 hover:text-cyan-300 text-sm">{calc.name}</Link>
              ))}
            </div>
          </div>
          <AdBottomBanner />
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PaceCalculatorPage;
